import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Copy, Save, RotateCcw, Settings2, Trash2, Plus, X, Check,
  WrapText, AlignLeft,
} from 'lucide-react';
import {
  fetchPromptTemplates,
  savePromptTemplates,
  buildNewTemplate,
} from '../api/proposalPrompts';
import {
  generateProposal,
  saveProposalDraft,
  fetchRecentProposals,
  deleteProposal,
} from '../api/proposals';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;

  h2 {
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.display};
    font-style: italic;
    font-weight: 500;
    font-size: 2rem;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  p {
    margin: 6px 0 0;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.95rem;
  }
`;

const ManageBtn = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.88rem;
  cursor: pointer;
  transition: border-color 160ms ease, color 160ms ease, background 160ms ease;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.04)`};
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.85rem;
  font-weight: 500;
`;

const Select = styled.select`
  width: 100%;
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 10px 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.92rem;
  outline: none;

  &:focus { border-color: ${({ theme }) => theme.colors.accent}; }
`;

const Input = styled.input`
  width: 100%;
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 10px 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.92rem;
  outline: none;

  &:focus { border-color: ${({ theme }) => theme.colors.accent}; }

  &::placeholder { color: ${({ theme }) => theme.colors.text.muted}; }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: ${(p) => p.$tall ? '320px' : '120px'};
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.92rem;
  line-height: 1.55;
  resize: vertical;
  outline: none;

  &:focus { border-color: ${({ theme }) => theme.colors.accent}; }

  &::placeholder { color: ${({ theme }) => theme.colors.text.muted}; }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 20px;
  background: ${({ theme }) => theme.colors.accent};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: 8px;
  color: #fff;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 160ms ease;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const GhostBtn = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.88rem;
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.04)`};
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Output = styled.div`
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.93rem;
  line-height: 1.65;
  white-space: pre-wrap;
  min-height: 320px;
  max-height: 540px;
  overflow-y: auto;
`;

const Placeholder = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

const ErrorBox = styled.div`
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.88rem;
`;

const SavedSection = styled.div`
  margin-top: 32px;

  h3 {
    margin: 0 0 12px;
    font-family: ${({ theme }) => theme.fonts.sans};
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: 1.1rem;
    font-weight: 600;
  }
`;

const SavedList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

const SavedCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  transition: border-color 160ms ease;

  &:hover { border-color: ${({ theme }) => theme.colors.accent}; }
`;

const SavedTitle = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.92rem;
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const SavedSnippet = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 0.82rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  padding: 2px;
  display: flex;
  transition: color 160ms ease;

  &:hover { color: #ef4444; }
`;

/* Prompt editor (syntax-highlighted textarea overlay) */

const EditorShell = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 160ms ease;

  &:focus-within { border-color: ${({ theme }) => theme.colors.accent}; }
`;

const EditorToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px 6px 12px;
  background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.03)`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToolbarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  background: ${({ theme, $on }) =>
    $on ? `rgba(${theme.colors.textRgb}, 0.08)` : 'transparent'};
  border: 1px solid ${({ theme, $on }) =>
    $on ? theme.colors.borderStrong : 'transparent'};
  border-radius: 6px;
  color: ${({ theme, $on }) =>
    $on ? theme.colors.text.primary : theme.colors.text.muted};
  font: inherit;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease, border-color 140ms ease;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.06)`};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Kbd = styled.kbd`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.7rem;
  padding: 2px 5px;
  border-radius: 4px;
  background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.08)`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EditorBody = styled.div`
  display: flex;
  height: ${(p) => p.$height || '60vh'};
  min-height: 320px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 13px;
  line-height: 1.65;
`;

const Gutter = styled.div`
  flex: 0 0 auto;
  padding: 12px 10px 12px 14px;
  color: ${({ theme }) => theme.colors.text.muted};
  background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.025)`};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  text-align: right;
  user-select: none;
  font-variant-numeric: tabular-nums;
  white-space: pre;
  overflow: hidden;
  opacity: 0.7;
`;

const EditorPane = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`;

const Highlight = styled.pre`
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 12px 14px;
  font: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: ${(p) => (p.$wrap ? 'pre-wrap' : 'pre')};
  word-break: ${(p) => (p.$wrap ? 'break-word' : 'normal')};
  overflow-wrap: ${(p) => (p.$wrap ? 'break-word' : 'normal')};
  overflow: auto;
  pointer-events: none;
  tab-size: 2;

  .tok-delim {
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 700;
  }
  .tok-block {
    background: rgba(255, 91, 58, 0.05);
    border-radius: 3px;
  }
  .tok-label {
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 600;
  }
  .tok-var {
    color: ${({ theme }) => theme.colors.accent};
    background: rgba(255, 91, 58, 0.1);
    padding: 0 3px;
    border-radius: 3px;
  }
  .tok-quote {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-style: italic;
  }
  .tok-comment {
    color: ${({ theme }) => theme.colors.text.muted};
    font-style: italic;
  }
`;

const HiddenTextarea = styled.textarea`
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 12px 14px;
  font: inherit;
  background: transparent;
  color: transparent;
  caret-color: ${({ theme }) => theme.colors.text.primary};
  border: none;
  outline: none;
  resize: none;
  white-space: ${(p) => (p.$wrap ? 'pre-wrap' : 'pre')};
  word-break: ${(p) => (p.$wrap ? 'break-word' : 'normal')};
  overflow-wrap: ${(p) => (p.$wrap ? 'break-word' : 'normal')};
  overflow: auto;
  tab-size: 2;

  &::selection {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.22)`};
    color: transparent;
  }
  &::-moz-selection {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.22)`};
    color: transparent;
  }
`;

const PLAIN_LABEL_WORDS = [
  'Sentence \\d+', 'Word \\d+', 'Paragraph \\d+',
  'Good example', 'Bad example', 'Example',
  'Why', 'How to apply', 'Tone', 'Length', 'Structure', 'Format',
  'Note', 'Rule', 'Constraint',
  'Greeting', 'The Hook', 'Hook', 'Intro', 'Body', 'Close', 'Closing',
  'CTA', 'Signature',
];

const PLAIN_PATTERNS = [
  { type: 'var', re: /\{\{?\s*[a-zA-Z_][\w-]*\s*\}?\}/g },
  { type: 'quote', re: /"[^"\n]{1,200}"/g },
  {
    type: 'label',
    re: new RegExp(
      `(?:^|(?<=\\n))(?:${PLAIN_LABEL_WORDS.join('|')})(?=[ \\t]*[\\u2014:\\-])`,
      'g'
    ),
  },
  { type: 'comment', re: /(?:^|(?<=\n))#[^\n]*/g },
];

const tokenizePlain = (text) => {
  const matches = [];
  for (const p of PLAIN_PATTERNS) {
    for (const m of text.matchAll(p.re)) {
      if (m[0].length === 0) continue;
      matches.push({ type: p.type, start: m.index, end: m.index + m[0].length, text: m[0] });
    }
  }
  matches.sort((a, b) => a.start - b.start || b.end - a.end);
  const picked = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start < cursor) continue;
    picked.push(m);
    cursor = m.end;
  }
  const out = [];
  let pos = 0;
  for (const m of picked) {
    if (m.start > pos) out.push({ type: 'text', text: text.slice(pos, m.start) });
    out.push({ type: m.type, text: m.text });
    pos = m.end;
  }
  if (pos < text.length) out.push({ type: 'text', text: text.slice(pos) });
  return out;
};

const tokenize = (text) => {
  const out = [];
  const blockRe = /\[\[([\s\S]*?)\]\]/g;
  let last = 0;
  for (const m of text.matchAll(blockRe)) {
    if (m.index > last) out.push(...tokenizePlain(text.slice(last, m.index)));
    out.push({ type: 'block', inner: tokenizePlain(m[1]) });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...tokenizePlain(text.slice(last)));
  return out;
};

const renderTokens = (tokens) =>
  tokens.map((t, i) => {
    if (t.type === 'text') {
      return <React.Fragment key={i}>{t.text}</React.Fragment>;
    }
    if (t.type === 'block') {
      return (
        <span key={i} className="tok-block">
          <span className="tok-delim">[[</span>
          {renderTokens(t.inner)}
          <span className="tok-delim">]]</span>
        </span>
      );
    }
    return (
      <span key={i} className={`tok-${t.type}`}>
        {t.text}
      </span>
    );
  });

const PromptEditor = ({ value, onChange, onSave, height }) => {
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);
  const gutterRef = useRef(null);
  const [wrap, setWrap] = useState(true);

  const lineCount = useMemo(() => value.split('\n').length, [value]);
  const charCount = value.length;

  const gutterText = useMemo(() => {
    let out = '';
    for (let i = 1; i <= lineCount; i += 1) out += i + '\n';
    return out;
  }, [lineCount]);

  const tokens = useMemo(() => tokenize(value), [value]);
  const trailingPad = value.length === 0 || value.endsWith('\n') ? '​' : null;

  const handleScroll = (e) => {
    const { scrollTop, scrollLeft } = e.target;
    if (highlightRef.current) {
      highlightRef.current.scrollTop = scrollTop;
      highlightRef.current.scrollLeft = scrollLeft;
    }
    if (gutterRef.current) {
      gutterRef.current.scrollTop = scrollTop;
    }
  };

  const handleKeyDown = (e) => {
    const isSave = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's';
    if (isSave) {
      e.preventDefault();
      onSave?.();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.target;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = value.slice(0, start) + '  ' + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 2;
      });
    }
  };

  return (
    <EditorShell>
      <EditorToolbar>
        <ToolbarGroup>
          <ToolbarBtn
            type="button"
            $on={wrap}
            onClick={() => setWrap((w) => !w)}
            title="Toggle soft wrap"
          >
            {wrap ? <WrapText size={13} /> : <AlignLeft size={13} />}
            {wrap ? 'Wrap' : 'No wrap'}
          </ToolbarBtn>
        </ToolbarGroup>
        <ToolbarGroup>
          <span>
            <Kbd>⌘</Kbd> <Kbd>S</Kbd> to save
          </span>
          <span style={{ opacity: 0.6 }}>·</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {lineCount} {lineCount === 1 ? 'line' : 'lines'} · {charCount} chars
          </span>
        </ToolbarGroup>
      </EditorToolbar>
      <EditorBody $height={height} data-lenis-prevent>
        <Gutter ref={gutterRef}>{gutterText}</Gutter>
        <EditorPane>
          <Highlight ref={highlightRef} $wrap={wrap} aria-hidden="true">
            {renderTokens(tokens)}
            {trailingPad}
          </Highlight>
          <HiddenTextarea
            ref={textareaRef}
            $wrap={wrap}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={handleScroll}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            wrap={wrap ? 'soft' : 'off'}
            data-lenis-prevent
          />
        </EditorPane>
      </EditorBody>
    </EditorShell>
  );
};

/* Template manager modal */

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalCard = styled(motion.div)`
  width: 100%;
  max-width: 1080px;
  max-height: 92vh;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.large};
`;

const ModalHeader = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.display};
    font-style: italic;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: 1.3rem;
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 20px;
  flex: 1;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const TemplateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TemplateRow = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  background: ${({ theme, $active }) =>
    $active ? `rgba(${theme.colors.textRgb}, 0.06)` : 'transparent'};
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.colors.borderStrong : theme.colors.border};
  border-left: 2px solid ${({ theme, $active }) =>
    $active ? theme.colors.accent : 'transparent'};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.sans};
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.88rem;
  transition: background 160ms ease, border-color 160ms ease;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.04)`};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const ModalFooter = styled.div`
  padding: 14px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const DirtyDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  flex: 0 0 auto;
  margin-left: 8px;
`;

const UnsavedNote = styled.span`
  margin-right: auto;
  color: ${({ theme }) => theme.colors.text.muted};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.82rem;
  font-style: italic;
`;

const ProposalMachinePage = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [proposal, setProposal] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const list = await fetchPromptTemplates();
      setTemplates(list);
      setSelectedTemplateId((prev) => prev || (list[0]?.id ?? ''));
    } catch (err) {
      console.error('Load templates failed:', err);
    }
  }, []);

  const loadDrafts = useCallback(async () => {
    try {
      const list = await fetchRecentProposals(20);
      setSavedDrafts(list);
    } catch (err) {
      console.error('Load drafts failed:', err);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadDrafts();
  }, [loadTemplates, loadDrafts]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      setError('Pick a template first.');
      return;
    }
    if (!jobDescription.trim()) {
      setError('Paste the Upwork job description first.');
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const text = await generateProposal({
        systemPrompt: selectedTemplate.systemPrompt,
        jobDescription,
      });
      setProposal(text);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!proposal) return;
    try {
      await navigator.clipboard.writeText(proposal);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveDraft = async () => {
    if (!proposal) return;
    try {
      await saveProposalDraft({
        templateId: selectedTemplate?.id,
        templateName: selectedTemplate?.name,
        jobDescription,
        generatedText: proposal,
      });
      await loadDrafts();
    } catch (err) {
      console.error(err);
      alert('Failed to save draft.');
    }
  };

  const handleOpenDraft = (draft) => {
    setJobDescription(draft.jobDescription || '');
    setProposal(draft.generatedText || '');
    if (draft.templateId && templates.find((t) => t.id === draft.templateId)) {
      setSelectedTemplateId(draft.templateId);
    }
  };

  const handleDeleteDraft = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this draft?')) return;
    try {
      await deleteProposal(id);
      await loadDrafts();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Header>
        <div>
          <h2>Proposal Machine</h2>
          <p>Generate Upwork proposals from a saved prompt template.</p>
        </div>
        <ManageBtn onClick={() => setShowTemplateManager(true)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
          <Settings2 size={16} />
          Manage Templates
        </ManageBtn>
      </Header>

      <Grid>
        <Panel>
          <div>
            <FieldLabel>Prompt Template</FieldLabel>
            <Select value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
              {templates.length === 0 && <option value="">Loading…</option>}
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Select>
          </div>

          <div>
            <FieldLabel>Job Description (paste the Upwork post)</FieldLabel>
            <TextArea
              $tall
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the full Upwork job description here…"
            />
          </div>

          {error && <ErrorBox>{error}</ErrorBox>}

          <Row>
            <PrimaryBtn
              onClick={handleGenerate}
              disabled={generating}
              whileHover={{ scale: generating ? 1 : 1.02 }}
              whileTap={{ scale: generating ? 1 : 0.98 }}
            >
              <Sparkles size={18} />
              {generating ? 'Generating…' : 'Generate Proposal'}
            </PrimaryBtn>
            {proposal && (
              <GhostBtn onClick={handleGenerate} disabled={generating}>
                <RotateCcw size={16} />
                Regenerate
              </GhostBtn>
            )}
          </Row>
        </Panel>

        <Panel>
          <FieldLabel>Generated Proposal</FieldLabel>
          <Output>
            {proposal ? proposal : <Placeholder>Your AI-generated proposal will appear here.</Placeholder>}
          </Output>
          <Row>
            <GhostBtn onClick={handleCopy} disabled={!proposal}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copied' : 'Copy'}
            </GhostBtn>
            <GhostBtn onClick={handleSaveDraft} disabled={!proposal}>
              <Save size={16} />
              Save Draft
            </GhostBtn>
          </Row>
        </Panel>
      </Grid>

      <SavedSection>
        <h3>Recent Drafts</h3>
        {savedDrafts.length === 0 ? (
          <Placeholder>No drafts saved yet.</Placeholder>
        ) : (
          <SavedList>
            {savedDrafts.map((d) => (
              <SavedCard key={d.id} onClick={() => handleOpenDraft(d)}>
                <SavedTitle>
                  <span>{d.templateName || 'Untitled draft'}</span>
                  <IconBtn onClick={(e) => handleDeleteDraft(d.id, e)} title="Delete">
                    <Trash2 size={14} />
                  </IconBtn>
                </SavedTitle>
                <SavedSnippet>{d.generatedText?.slice(0, 220) || '—'}</SavedSnippet>
              </SavedCard>
            ))}
          </SavedList>
        )}
      </SavedSection>

      <AnimatePresence>
        {showTemplateManager && (
          <TemplateManager
            initialTemplates={templates}
            onClose={() => setShowTemplateManager(false)}
            onSaved={(next) => {
              setTemplates(next);
              if (!next.find((t) => t.id === selectedTemplateId)) {
                setSelectedTemplateId(next[0]?.id ?? '');
              }
              setShowTemplateManager(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const isTemplateDirty = (current, initialList) => {
  const initial = initialList.find((t) => t.id === current.id);
  if (!initial) return true;
  return initial.name !== current.name || initial.systemPrompt !== current.systemPrompt;
};

const TemplateManager = ({ initialTemplates, onClose, onSaved }) => {
  const [list, setList] = useState(initialTemplates);
  const [activeId, setActiveId] = useState(initialTemplates[0]?.id || '');
  const [saving, setSaving] = useState(false);

  const active = list.find((t) => t.id === activeId);
  const hasUnsavedChanges = useMemo(() => {
    if (list.length !== initialTemplates.length) return true;
    return list.some((t) => isTemplateDirty(t, initialTemplates));
  }, [list, initialTemplates]);

  const updateActive = (patch) => {
    setList((prev) => prev.map((t) => (t.id === activeId ? { ...t, ...patch } : t)));
  };

  const addTemplate = () => {
    const tpl = buildNewTemplate();
    setList((prev) => [...prev, tpl]);
    setActiveId(tpl.id);
  };

  const deleteActive = () => {
    if (list.length <= 1) {
      alert('You need at least one template.');
      return;
    }
    if (!window.confirm(`Delete template "${active?.name}"?`)) return;
    const next = list.filter((t) => t.id !== activeId);
    setList(next);
    setActiveId(next[0]?.id || '');
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await savePromptTemplates(list);
      onSaved(list);
    } catch (err) {
      console.error(err);
      alert('Failed to save templates.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalCard
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
      >
        <ModalHeader>
          <h3>Manage Prompt Templates</h3>
          <IconBtn onClick={onClose} title="Close"><X size={20} /></IconBtn>
        </ModalHeader>
        <ModalBody data-lenis-prevent>
          <TemplateList>
            {list.map((t) => {
              const dirty = isTemplateDirty(t, initialTemplates);
              return (
                <TemplateRow key={t.id} $active={t.id === activeId} onClick={() => setActiveId(t.id)}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.name || 'Untitled'}
                  </span>
                  {dirty && <DirtyDot title="Unsaved changes" />}
                </TemplateRow>
              );
            })}
            <ManageBtn onClick={addTemplate} whileHover={{ scale: 1.02 }} style={{ marginTop: 8, justifyContent: 'center' }}>
              <Plus size={14} />
              New Template
            </ManageBtn>
          </TemplateList>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
            {active ? (
              <>
                <div>
                  <FieldLabel>Name</FieldLabel>
                  <Input value={active.name} onChange={(e) => updateActive({ name: e.target.value })} />
                </div>
                <div>
                  <FieldLabel>System Prompt</FieldLabel>
                  <PromptEditor
                    value={active.systemPrompt}
                    onChange={(next) => updateActive({ systemPrompt: next })}
                    onSave={handleSaveAll}
                    height="56vh"
                  />
                </div>
                <div>
                  <GhostBtn onClick={deleteActive}>
                    <Trash2 size={14} />
                    Delete this template
                  </GhostBtn>
                </div>
              </>
            ) : (
              <Placeholder>Select or create a template.</Placeholder>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          {hasUnsavedChanges && <UnsavedNote>Unsaved changes</UnsavedNote>}
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={handleSaveAll} disabled={saving || !hasUnsavedChanges}>
            <Save size={16} />
            {saving ? 'Saving…' : 'Save All'}
          </PrimaryBtn>
        </ModalFooter>
      </ModalCard>
    </ModalOverlay>
  );
};

export default ProposalMachinePage;
